import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GetFileInfoProps, getFileOrderInsights } from 'api/file/file-api';
import { getJackets, getStandaloneEndorsements } from 'api/jacket-api';
import { getPropertyCounty } from 'features/files/file-utilities';
import { isCountyRequiredError, mapOrderInsightJacketsToJackets } from 'features/jackets/jacket-utilities';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import useAsync from 'hooks/useAsync';
import { FC, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { Notification } from 'ui-kit/components/notification/Notification';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { useViewState } from '../../hooks/ViewStateContext';
import { fileStatuses } from '../constants';
import { JacketStatus, StandaloneEndorsementStatus } from './constants';
import CountySelector from './CountySelector';
import JacketFC from './Jacket';
import './JacketsContainer.scss';
import StandaloneEndorsementFC from './StandaloneEndorsement';
import { Jacket, StandaloneEndorsement, Warning } from './types';
interface JacketsContainerProps {
  showStandAlone?: boolean | false;
}
const JacketsContainer: FC<JacketsContainerProps> = () => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        background: theme.palette.common.white,
      },
    }),
  );
  const classes = useStyles();
  const { fileId } = useParams<GetFileInfoProps>();
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const {
    fileData,
    setFileData,
    loadFileOrderInsightsData,
    loadFileData,
    fileRefreshTrigger,
    triggerFileRefresh,
    setSessionFileId,
    fileOrderInsightsData,
  } = fileDataCtx;

  const { getAccessToken } = useAuth();
  const preventJacketWithSE = useFeatureFlag(FeatureFlag.JACKET_ANS_9276_PREVENT_WITH_STANDALONE_ENDORSEMENT, false);
  interface CustomizedState {
    showSE: string;
  }
  const location = useLocation();
  const state = location.state as CustomizedState;
  const myState = state?.showSE;

  const [showJacket, setShowJacket] = useState(false);
  const [isAddNewJacket, setIsAddNewJacket] = useState(false);
  const [showJacketList, setShowJacketList] = useState(false);
  const [showSaEndorsement, setShowSaEndorsement] = useState<boolean>(false);
  const [standaloneEndorsements, setStandaloneEndorsements] = useState<StandaloneEndorsement[]>([]);
  const [isCountySelected, setIsCountySelected] = useState<boolean>(true);

  const [warnings, setWarning] = useState<Warning[]>([]);
  const [showWarnings, setShowWarnings] = useState(false);
  const { routerParamValue, setRouterParamValue } = useViewState();

  const fetchJackets = async (): Promise<Jacket[]> => {
    const token = await getAccessToken();
    const county = fileData?.properties?.[0]?.county;
    const isClosedFile = fileData?.fileStatus === fileStatuses.closed;

    try {
      const jackets = await getJackets(fileId ?? '', token);
      return jackets ?? [];
    } catch (error) {
      if (!isClosedFile || county || !isCountyRequiredError(error)) {
        throw error;
      }

      const cachedOrderInsightJackets =
        fileOrderInsightsData?.fileId === fileId ? fileOrderInsightsData?.jackets ?? [] : [];
      const orderInsightJackets =
        cachedOrderInsightJackets.length > 0
          ? cachedOrderInsightJackets
          : (await getFileOrderInsights(fileId ?? '', token).catch(() => null))?.jackets ?? [];

      return mapOrderInsightJacketsToJackets(orderInsightJackets);
    }
  };

  const {
    execute: executeGetJackets,
    value: getJacketsValue,
    status: getJacketsStatus,
    reset: resetGetJackets,
  } = useAsync<Jacket[]>(fetchJackets, false);

  useEffect(() => {
    setRouterParamValue(fileId || '');
  }, [routerParamValue, setRouterParamValue]);

  useEffect(() => {
    resetGetJackets();
    setShowJacketList(false);
    setShowJacket(false);
    setIsAddNewJacket(false);
    setShowSaEndorsement(false);
  }, [fileId, resetGetJackets]);

  useEffect(() => {
    if (!fileData?.fileId && fileId && !sessionStorage.getItem('fileId')) {
      setSessionFileId?.(fileId);
      loadFileData?.({ fileId });
    }

    if (fileData && fileData.fileId !== '') {
      const county = fileData?.properties?.[0]?.county;
      const isClosedFile = fileData.fileStatus === fileStatuses.closed;

      if (county || isClosedFile) {
        executeGetJackets().catch(() => undefined);
        setIsCountySelected(true);
      } else {
        setIsCountySelected(false);
      }
    }
  }, [fileId, fileData?.fileId, fileData?.fileStatus]);

  const handleNewJacket = () => {
    setShowJacketList(false);
    setShowJacket(true);
    setIsAddNewJacket(true);
    setShowWarnings(false);
  };

  const handleStandaloneDelete = () => {
    setStandaloneEndorsements([]);
    setShowSaEndorsement(false);
    if (fileId) loadFileData && loadFileData({ fileId });
  };

  const hideCreateJacket = () => {
    if (getJacketsValue && getJacketsValue.length > 0) {
      setShowJacketList(true);
    }
    setShowJacket(false);
    setIsAddNewJacket(false);
  };

  const refreshJacketsData = () => {
    executeGetJackets();
    hideCreateJacket();
  };

  const addWarnings = (warnings: Warning[]) => {
    if (warnings) {
      setWarning(warnings);
      setShowWarnings(true);
    } else {
      setWarning([]);
      setShowWarnings(false);
    }
  };

  const hideWarningOnCancel = (isWarning: boolean) => {
    {
      isWarning ? setShowWarnings(true) : setShowWarnings(false);
    }
  };

  useEffect(() => {
    if (myState) {
      setShowSaEndorsement(true);
    }
  }, [myState]);

  useEffect(() => {
    if (getJacketsValue && getJacketsValue.length > 0) {
      const hasNonVoidJackets = getJacketsValue.some(
        (jacket) => !([JacketStatus.void, JacketStatus.pendingVoid] as string[]).includes(jacket.policyStatus),
      );
      if (hasNonVoidJackets) {
        setFileData &&
          setFileData((prevFileData: any) => ({
            ...prevFileData,
            serviceStatus: {
              ...prevFileData.serviceStatus,
              hasActiveJacket: true,
            },
          }));
      }
      setShowJacketList(true);
      setShowJacket(false);
      setIsAddNewJacket(false);
      setShowSaEndorsement(false);
    } else if (getJacketsValue?.length === 0 && getJacketsStatus === 'success') {
      handleNewJacket();
    }
  }, [getJacketsValue, getJacketsStatus]);

  const fetchStandaloneEndorsements = async (): Promise<StandaloneEndorsement[]> => {
    const token = await getAccessToken();
    return getStandaloneEndorsements(fileId ?? '', token);
  };

  const {
    execute: executeGetSaEndorsements,
    value: getSaEndorsementsValue,
    status: getSaEndorsementsStatus,
  } = useAsync<StandaloneEndorsement[]>(fetchStandaloneEndorsements, false);

  useEffect(() => {
    if (fileId) executeGetSaEndorsements();
  }, [fileId]);

  useEffect(() => {
    if (getSaEndorsementsValue && getSaEndorsementsValue.length > 0) {
      if (preventJacketWithSE) {
        setFileData &&
          setFileData((prevFileData: any) => ({
            ...prevFileData,
            serviceStatus: {
              ...prevFileData.serviceStatus,
              hasStandAloneEndorsement: true,
            },
          }));
      }
      setStandaloneEndorsements(getSaEndorsementsValue);
      if (!(getJacketsValue && getJacketsValue.length > 0)) {
        setShowSaEndorsement(true);
      }
    } else if (getSaEndorsementsValue && getSaEndorsementsValue.length === 0) {
      if (preventJacketWithSE) {
        setFileData &&
          setFileData((prevFileData: any) => ({
            ...prevFileData,
            serviceStatus: {
              ...prevFileData.serviceStatus,
              hasStandAloneEndorsement: false,
            },
          }));
      }
      setStandaloneEndorsements([]);
    }
  }, [getSaEndorsementsValue]);

  useEffect(() => {
    if (fileRefreshTrigger) {
      executeGetJackets();
      executeGetSaEndorsements();
      triggerFileRefresh?.(false);
    }
  }, [fileRefreshTrigger]);

  const hasActiveJackets =
    getJacketsValue?.some(
      (jacket) => !([JacketStatus.void, JacketStatus.pendingVoid] as string[]).includes(jacket.policyStatus),
    ) ?? false;

  return (
    <>
      <div className={classes.root}>
        <div className="paper-layout contained">
          <div className={getJacketsStatus === 'pending' ? 'loading-container' : ''}>
            <LoadingSpinner status={getJacketsStatus} size={30} />
          </div>
          {preventJacketWithSE && standaloneEndorsements?.length > 0 && hasActiveJackets && !showSaEndorsement && (
            <Notification
              severity="warning"
              msg="This file contains both a jacket and a standalone endorsement, which is no longer permitted. To proceed, please either void the jacket or delete the standalone endorsement below."
              inline
            />
          )}
          {!showSaEndorsement &&
            showJacketList &&
            getJacketsStatus !== 'pending' &&
            !(preventJacketWithSE && standaloneEndorsements?.length > 0 && !hasActiveJackets) && (
              <JacketFC
                fileId={fileId}
                state={fileData?.propertyState}
                county={getPropertyCounty(fileData ?? {})}
                underwriter={fileData?.underwriterCode}
                setJacketFlag={setShowJacket}
                accountNumber={fileData?.accountId}
                jacketList={getJacketsValue ?? []}
                fileData={fileData}
                loadFileOrderInsightsData={loadFileOrderInsightsData}
                isAddNewJacketClicked={false}
                lenders={fileData?.lenders}
                OnCancel={hideCreateJacket}
                handleAddNewJacket={handleNewJacket}
                refreshJacketsData={refreshJacketsData}
                addWarnings={addWarnings}
                warnings={warnings}
                hideWarningOnCancel={hideWarningOnCancel}
                showWarnings={showWarnings}
              />
            )}

          {showSaEndorsement && getJacketsStatus !== 'pending' && (
            <StandaloneEndorsementFC
              saEndorsements={standaloneEndorsements ?? []}
              fileId={fileId}
              state={fileData?.propertyState}
              underwriter={fileData?.underwriterCode}
              setStandaloneFlag={setShowSaEndorsement}
              handleDelete={handleStandaloneDelete}
              filestatus={fileData?.fileStatus}
            />
          )}

          {preventJacketWithSE &&
            !showSaEndorsement &&
            standaloneEndorsements?.length > 0 &&
            getJacketsStatus !== 'pending' && (
              <StandaloneEndorsementFC
                saEndorsements={standaloneEndorsements}
                fileId={fileId}
                state={fileData?.propertyState}
                underwriter={fileData?.underwriterCode}
                setStandaloneFlag={setShowSaEndorsement}
                handleDelete={handleStandaloneDelete}
                filestatus={fileData?.fileStatus}
              />
            )}

          {getSaEndorsementsStatus !== StandaloneEndorsementStatus.PENDING &&
            !showSaEndorsement &&
            showJacket &&
            standaloneEndorsements?.length === 0 && (
              <JacketFC
                fileId={fileId}
                state={fileData?.propertyState}
                county={getPropertyCounty(fileData ?? {})}
                underwriter={fileData?.underwriterCode}
                setJacketFlag={setShowJacket}
                accountNumber={fileData?.accountId}
                jacketList={getJacketsValue ?? []}
                isAddNewJacketClicked={isAddNewJacket}
                lenders={fileData?.lenders}
                OnCancel={hideCreateJacket}
                refreshJacketsData={refreshJacketsData}
                fileData={fileData}
                loadFileOrderInsightsData={loadFileOrderInsightsData}
                addWarnings={addWarnings}
                warnings={warnings}
                hideWarningOnCancel={hideWarningOnCancel}
                showWarnings={showWarnings}
              />
            )}

          {!isCountySelected && fileData?.fileStatus !== fileStatuses.closed && (
            <CountySelector executeGetJackets={executeGetJackets} setIsCountySelected={setIsCountySelected} />
          )}
        </div>
      </div>
    </>
  );
};

export default JacketsContainer;
